import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import NavigationHeader from '../../components/NavigationHeader';
import SectionScrollIndicator from '../../components/SectionScrollIndicator';
import ProjectCard from './components/ProjectCard';
import ProjectFilter from './components/ProjectFilter';
import ProjectDetailModal from './components/ProjectDetailModal';
import ProjectStats from './components/ProjectStats';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const ProjectPortfolio = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTechnology, setSelectedTechnology] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const projectsPerPage = 6;
  const navigate = useNavigate();

  // Project data from home landing page
  const projects = [
    {
      id: 0,
      title: "KiviCare MediFlow",
      description: "A comprehensive, white-label clinic management platform with native dental charting, dual telemedicine support, and multi-currency billing.",
      fullDescription: "KiviCare MediFlow is a next-generation, white-label clinic management platform built for modern healthcare providers. It replaces fragmented scheduling tools, paper records, and disconnected communication channels with a single, intelligent workspace.\n\nThe platform features native dental charting, dual telemedicine providers (Zoom & Google Meet), multi-currency billing (Stripe, PayPal, Razorpay), and a powerful white-label engine. Built with a modern Node.js/Express backend and a responsive Next.js frontend, the system provides a seamless experience for doctors, receptionists, administrators, and patients.",
      category: "Full Stack",
      technologies: ["Next.js", "Node.js", "Express", "MongoDB", "Socket.io", "React Query", "TailwindCSS", "Zustand", "Stripe", "Zoom API", "Twilio"],
      image: "/assets/images/kivcare_mern.png",
      liveUrl: "https://mediflow.titanscale.net",
      githubUrl: "",
      status: "completed",
      completedDate: "June 2026",
      duration: "6 months",
      teamSize: "Lead Developer",
      codeLines: "50,000+",
      performance: "99.9% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "Healthcare providers needed a modern, unified platform to manage scheduling, patient records, billing, and communication, replacing fragmented tools with a single, intelligent workspace capable of handling telemedicine and dental charting.",
      solutionApproach: "Developed a comprehensive clinic management platform using Next.js and Node.js. Implemented multi-tenant architecture, multi-role access control, native dental charting, embedded telemedicine via Zoom/Meet, and comprehensive multi-currency billing.",
      keyFeatures: [
        "Smart booking system with real-time slot availability",
        "Permanent, structured health records and dental charting",
        "Telemedicine support (Zoom & Google Meet)",
        "Multi-currency billing (Stripe, PayPal, Razorpay)",
        "Multi-channel notifications (In-app, Email, SMS, WhatsApp)",
        "Role-specific analytics and reporting dashboards",
        "True white-label engine for brand customization",
        "Self-service demo provisioning and auto-cleanup"
      ],
      challenges: [
        {
          title: "Multi-Tenancy and Data Isolation",
          solution: "Implemented a shared-database, shared-collection multi-tenancy model with tenant isolation enforced at the application layer using JWT role enforcement and clinic-scoped queries."
        },
        {
          title: "Dual Telemedicine Integration",
          solution: "Built seamless integrations with both Zoom (OAuth/S2S) and Google Meet, allowing doctors to switch providers per appointment without losing scheduling sync."
        },
        {
          title: "Complex Billing and Payments",
          solution: "Developed a multi-gateway payment system integrating Stripe, PayPal, and Razorpay natively with full order/capture flows, supporting partial payments and automatic recurring expenses."
        }
      ],
      screenshots: [
        {
          url: "/assets/images/kivcare_mern.png",
          caption: "KiviCare MediFlow Overview"
        },
        {
          url: "/assets/images/dashboard_kivicare.png",
          caption: "Advanced Features & Settings"
        },
        {
          url: "/assets/images/advance_kivicare.png",
          caption: "KiviCare MediFlow Dashboard"
        }
      ]
    },
    {
      id: 1,
      title: "E-Commerce Platform",
      description: "Node.js, Express, MongoDB powered ecommerce site with shopping cart, payments, search and filters, admin portal for inventory and order management",
      fullDescription: `A comprehensive e-commerce platform built with Node.js, Express, and MongoDB. The application features a complete shopping experience with shopping cart functionality, secure payment processing via Stripe, advanced search and filtering capabilities, and a robust admin portal for inventory and order management.

The platform is designed to handle real-world e-commerce scenarios with features like product catalog management, user authentication, order tracking, and payment processing. Built with EJS templating and Bootstrap for a responsive, user-friendly interface.`,
      category: "Full Stack",
      technologies: ["Node.js", "Express", "EJS", "MongoDB", "Stripe", "Bootstrap"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/v1764605609/3a46047f-2e47-4f8e-af60-ddcf3de61b9f.png",
      liveUrl: "https://e-commerce-63vv.onrender.com/",
      githubUrl: "https://github.com/DishantMadariya/E-commerce",
      status: "completed",
      completedDate: "Dec 2024",
      duration: "3 months",
      teamSize: "1 developer",
      codeLines: "15,000+",
      performance: "98% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "Businesses needed a complete e-commerce solution with shopping cart, payment processing, and admin management capabilities that could be easily deployed and maintained.",
      solutionApproach: "Developed a full-stack e-commerce platform using Node.js and Express for the backend, MongoDB for data storage, and integrated Stripe for secure payment processing. Implemented a comprehensive admin portal for inventory and order management.",
      keyFeatures: [
        "Shopping cart with persistent session management",
        "Secure payment processing with Stripe integration",
        "Advanced product search and filtering",
        "Admin portal for inventory management",
        "Order tracking and management system",
        "User authentication and authorization",
        "Responsive design with Bootstrap",
        "Product catalog with categories"
      ],
      challenges: [
        {
          title: "Payment Integration",
          solution: "Integrated Stripe payment gateway with proper error handling and transaction management to ensure secure and reliable payment processing."
        },
        {
          title: "Session Management",
          solution: "Implemented persistent shopping cart using session storage to maintain cart state across page refreshes and user sessions."
        },
        {
          title: "Admin Portal Security",
          solution: "Created role-based access control system to secure admin routes and ensure only authorized users can manage inventory and orders."
        }
      ],
      screenshots: [
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/v1764605609/3a46047f-2e47-4f8e-af60-ddcf3de61b9f.png",
          caption: "E-commerce platform homepage with product catalog"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/v1764605748/e9f0642d-ff26-420e-b9cf-ae4e6b1da007.png",
          caption: "Admin portal for inventory and order management"
        }
      ]
    },
    {
      id: 2,
      title: "NuxHealth (HMS) (Backend Development)",
      description: "A comprehensive hospital management system built with Node.js and Express.js that streamlines hospital operations including appointment scheduling, patient management, billing, prescriptions, and real-time communication between doctors and patients.",
      fullDescription: "A full-featured Hospital Management System (HMS) built with the Node.js/Express.js backend stack. The system provides a complete solution for managing hospital operations with multi-role authentication (Admin, Doctor, Patient, Receptionist), appointment scheduling (both online and onsite), prescription management, billing and payment processing, insurance management, real-time chat, and notification systems.\n\nThe application features JWT-based authentication with role-based access control using Passport.js, real-time communication via Socket.io, secure payment processing through Razorpay integration, cloud-based image storage with Cloudinary, and comprehensive notification systems using email (Nodemailer) and SMS (Twilio). The system includes Swagger API documentation, CORS support, and robust error handling throughout.",
      category: "Backend Development",
      technologies: [
        "Node.js",
        "Express.js",
        "MongoDB",
        "Mongoose",
        "JWT",
        "Passport.js",
        "Socket.io",
        "Razorpay",
        "Cloudinary",
        "Multer",
        "Twilio",
        "Nodemailer",
        "Swagger",
        "CORS",
        "bcrypt",
        "Moment.js"
      ],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/v1764605804/c54cbdd9-8fa9-41b9-bc54-fa7748f90c66.png",
      liveUrl: "https://nuxhealth-backend-dpti.onrender.com/api-docs/",
      githubUrl: "",
      status: "completed",
      completedDate: "2024",
      duration: "3-4 months",
      teamSize: "1-2 developers",
      codeLines: "15,000+",
      performance: "99% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "Hospitals and healthcare facilities needed a comprehensive digital solution to manage appointments, patient records, prescriptions, billing, and communication between healthcare providers and patients. Traditional paper-based systems were inefficient, error-prone, and lacked real-time communication capabilities.",
      solutionApproach: "Developed a robust backend system using Node.js and Express.js with MongoDB for data storage. Implemented multi-role authentication with JWT and Passport.js for secure access control. Integrated real-time features using Socket.io for notifications and chat functionality. Added payment processing with Razorpay, cloud storage with Cloudinary, and communication services with Twilio and Nodemailer. Created a RESTful API architecture with comprehensive error handling and Swagger documentation.",
      keyFeatures: [
        "Multi-role authentication system (Admin, Doctor, Patient, Receptionist) with JWT and Passport.js",
        "Hospital management with location-based search (country, state, city)",
        "Appointment scheduling system supporting both online and onsite consultations",
        "Doctor session management with morning/evening slots and availability tracking",
        "Holiday management for doctors with session-specific availability",
        "Prescription management with medication tracking and instructions",
        "Billing system with tax calculation, discounts, and multiple payment methods",
        "Insurance management with claim processing and coverage tracking",
        "Real-time chat system between doctors and patients with media support",
        "Real-time notifications via Socket.io for appointments, bills, and prescriptions",
        "Payment integration with Razorpay for online transactions",
        "Email notifications using Nodemailer for OTP and updates",
        "SMS notifications using Twilio for OTP verification",
        "Cloud-based image storage with Cloudinary for profile pictures and documents",
        "Patient records and appointment history tracking",
        "Dashboard analytics for different user roles",
        "Search functionality for hospitals, doctors, and appointments",
        "API documentation with Swagger/OpenAPI",
        "Role-based access control and authorization",
        "Comprehensive error handling and validation"
      ],
      challenges: [
        {
          title: "Multi-Role Authentication and Authorization",
          solution: "Implemented JWT-based authentication with separate secrets for each role (Admin, Doctor, Patient, Receptionist) using Passport.js strategies. Created middleware for role-based route protection ensuring users can only access resources appropriate to their role."
        },
        {
          title: "Real-Time Communication and Notifications",
          solution: "Integrated Socket.io for real-time bidirectional communication. Implemented hospital-based room management for notifications and user-specific socket connections for chat. Created a notification service that saves notifications to the database and emits them in real-time to online users."
        },
        {
          title: "Appointment Scheduling and Conflict Management",
          solution: "Developed a sophisticated appointment system that checks for conflicts between doctor and patient schedules. Implemented dynamic time slot generation based on doctor's morning/evening sessions and duration. Added holiday management to block unavailable time slots automatically."
        },
        {
          title: "Payment Processing and Billing",
          solution: "Integrated Razorpay payment gateway with secure payment verification using HMAC signatures. Implemented comprehensive billing system with tax calculation, discounts, insurance claims, and due amount tracking. Added support for multiple payment types (Online, Cash, Insurance) with proper validation."
        },
        {
          title: "File Upload and Cloud Storage",
          solution: "Configured Multer for handling file uploads and integrated Cloudinary for cloud-based image storage. Implemented services for deleting images when needed and handling multiple file types including profile pictures and chat media."
        },
        {
          title: "Insurance Claim Processing",
          solution: "Created an insurance management system that tracks insurance companies, plans, claim amounts, and claimed amounts. Implemented logic to calculate due amounts when insurance coverage is partial, ensuring accurate billing for patients with insurance."
        }
      ]
    },
    {
      id: 3,
      title: "Thread Clone",
      description: "Thread Clone is a social media platform that allows users to create, share, and interact with threads. It is built with Node.js, Express, MongoDB, and EJS.",
      fullDescription: `A modern social media platform inspired by Threads, built with Next.js, Node.js, and MongoDB. The application allows users to create, share, and interact with threads in a clean and intuitive interface.

Built with TypeScript for type safety, Tailwind CSS for styling, and integrated with Clerk for authentication, Uploadthing for media uploads, and Radix UI for accessible components. The platform features real-time updates, user profiles, thread interactions, and a responsive design.`,
      category: "Full Stack",
      technologies: ["Next.js", "Node.js", "MongoDB", "Tailwind CSS", "TypeScript", "Radix Ui", "Clerk", "Uploadthing", "Zod"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/7d82f48f-8cf2-4c81-ba06-65e8f28727d4.png",
      liveUrl: "https://threads-sage-rho.vercel.app/",
      githubUrl: "https://github.com/DishantMadariya/Threads",
      status: "completed",
      completedDate: "Nov 2024",
      duration: "4 months",
      teamSize: "1 developer",
      codeLines: "20,000+",
      performance: "99% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "Users needed a modern social media platform for sharing short-form content and engaging in threaded conversations with a clean, intuitive interface.",
      solutionApproach: "Developed a full-stack social media application using Next.js for the frontend, Node.js and MongoDB for the backend, with Clerk for authentication and Uploadthing for media handling. Implemented real-time features and responsive design.",
      keyFeatures: [
        "User authentication with Clerk",
        "Create and share threads",
        "Real-time thread interactions",
        "User profiles and activity feed",
        "Media uploads with Uploadthing",
        "Responsive design with Tailwind CSS",
        "Type-safe development with TypeScript",
        "Accessible UI components with Radix UI"
      ],
      challenges: [
        {
          title: "Real-time Updates",
          solution: "Implemented real-time thread updates and interactions using Next.js server actions and MongoDB change streams to provide instant feedback to users."
        },
        {
          title: "Media Upload Management",
          solution: "Integrated Uploadthing for efficient media uploads with proper file validation, compression, and CDN delivery for optimal performance."
        },
        {
          title: "Type Safety",
          solution: "Used TypeScript and Zod for end-to-end type safety, ensuring data validation and type checking across the entire application stack."
        }
      ],
      screenshots: [
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/7d82f48f-8cf2-4c81-ba06-65e8f28727d4.png",
          caption: "Thread Clone homepage with feed"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/0b6622e1-2a53-43ea-bffa-89244d8f5b98.png",
          caption: "User profile and thread creation interface"
        }
      ]
    },
    {
      id: 4,
      title: "Ghost Feedback System",
      description: "A secure, anonymous suggestion box system built with the MERN stack (MongoDB, Express.js, React, Node.js) that allows employees to submit feedback and suggestions without revealing their identity.",
      fullDescription: `A secure, anonymous feedback system built with the MERN stack (MongoDB, Express.js, React, Node.js). The application allows employees to submit feedback and suggestions without revealing their identity, promoting honest and open communication.

The system features JWT-based authentication for administrators, express-validator for input validation, and security measures including Helmet, CORS, and rate limiting to protect against common web vulnerabilities. Built with React for a modern, responsive user interface.`,
      category: "Full Stack",
      technologies: ["React", "Node.js", "Express", "MongoDB", "JWT", "Express-validator", "Helmet", "CORS", "Rate Limiting"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/e0335e5a-8386-4c48-85a2-4643bb5ed03c.png",
      liveUrl: "https://ghost-feedbacksystem.vercel.app/",
      githubUrl: "https://github.com/DishantMadariya/ghost-feedbacksystem",
      status: "completed",
      completedDate: "Oct 2024",
      duration: "15 days",
      teamSize: "1 developer",
      codeLines: "12,000+",
      performance: "99.5% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "Organizations needed a secure, anonymous feedback system that would encourage honest employee feedback while maintaining security and preventing abuse.",
      solutionApproach: "Developed a MERN stack application with anonymous feedback submission, JWT authentication for admin access, and comprehensive security measures including rate limiting, CORS, and input validation to ensure system integrity.",
      keyFeatures: [
        "Anonymous feedback submission",
        "JWT-based admin authentication",
        "Secure API with Helmet and CORS",
        "Rate limiting to prevent abuse",
        "Input validation with express-validator",
        "Admin dashboard for feedback management",
        "Responsive React frontend",
        "MongoDB for data storage"
      ],
      challenges: [
        {
          title: "Anonymity and Security",
          solution: "Implemented anonymous feedback system with proper security measures including rate limiting and input validation to prevent abuse while maintaining user anonymity."
        },
        {
          title: "Admin Authentication",
          solution: "Used JWT tokens for secure admin authentication with proper token expiration and refresh mechanisms to ensure secure access to admin features."
        },
        {
          title: "API Security",
          solution: "Integrated Helmet for HTTP header security, CORS for cross-origin resource sharing, and rate limiting to protect against common attacks and abuse."
        }
      ],
      screenshots: [
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/e0335e5a-8386-4c48-85a2-4643bb5ed03c.png",
          caption: "Anonymous feedback submission interface"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/6173bf71-c946-431c-858f-a2f588b8dae8.png",
          caption: "Admin dashboard for feedback management"
        }
      ]
    },
    {
      id: 5,
      title: "KiviCare - Emanuele (PrimaClinic)",
      description: "Customization and development of a comprehensive Healthcare ERP system for PrimaClinic with advanced billing, appointment management, encounter tracking, and multi-clinic support built on WordPress/Elementor.",
      fullDescription: `Kivicare is a comprehensive Healthcare ERP system being customized and developed for PrimaClinic, a healthcare management platform. The system provides end-to-end clinic management including advanced billing with multiple bill types (Company Bill, Doctor Bill, Company Receipt, Service Record), appointment scheduling with overbooking capabilities, encounter management with rich text support, and multi-clinic operations.

The project involves extensive customization of the existing Kivicare WordPress plugin, integrating Elementor for frontend customization, and developing new features to meet specific Italian healthcare billing requirements including VAT management, "Bollo Virtuale" tax calculations, and compliance with Italian tax regulations.

Working as part of a 2-member team at Iqonic Design, focusing on backend customization, billing system enhancements, appointment management improvements, and WordPress/Elementor integration.`,
      category: "WordPress",
      technologies: ["WordPress", "PHP", "Elementor", "MySQL", "JavaScript", "jQuery", "REST API", "WooCommerce"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/6c608a61-6aa1-4fca-82a1-13df1e6d8f39.png",
      liveUrl: "https://primaclinic.it/",
      githubUrl: "",
      status: "in-progress",
      completedDate: "Ongoing",
      duration: "Ongoing",
      teamSize: "2 developers",
      codeLines: "50,000+",
      performance: "99% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "PrimaClinic needed a comprehensive healthcare management system with Italian-specific billing requirements, advanced appointment scheduling, multi-clinic support, and seamless integration with their existing WordPress infrastructure. The system required customization to handle complex billing scenarios, tax calculations, and multi-doctor workflows.",
      solutionApproach: "Developing and customizing the Kivicare Healthcare ERP system on WordPress/Elementor platform, implementing advanced billing features with multiple bill types, tax management including Italian 'Bollo Virtuale' calculations, enhanced appointment scheduling with overbooking capabilities, and comprehensive encounter management with rich text support.",
      keyFeatures: [
        "Advanced billing system with 4 bill types (Company Bill, Doctor Bill, Company Receipt, Service Record)",
        "Italian tax compliance with VAT management and Bollo Virtuale calculations",
        "Multi-clinic support with cross-clinic receptionist access",
        "Enhanced appointment scheduling with overbooking and availability visualization",
        "Rich text encounter notes with templates and editing capabilities",
        "Monthly payroll calculation with percentage and fixed fee support",
        "Time-based service scheduling with multiple slots per day",
        "No-show management with automated notifications",
        "Online payment controls per doctor and clinic",
        "Doctor occupancy percentage tracking",
        "Multi-doctor widget with next availability display",
        "Automated agenda printing and email notifications",
        "Visit control reminders with customizable templates",
        "WordPress/Elementor integration for frontend customization"
      ],
      challenges: [
        {
          title: "Complex Billing Requirements",
          solution: "Implemented a flexible billing system supporting multiple bill types with different data sources (company vs doctor), automatic tax calculations including Italian Bollo Virtuale, and bill type memory per doctor for improved UX."
        },
        {
          title: "Multi-Clinic Architecture",
          solution: "Designed a scalable multi-clinic system allowing receptionists to access multiple clinics, doctors to work across locations, and maintaining separate billing and scheduling per clinic while sharing patient data."
        },
        {
          title: "Appointment Scheduling Complexity",
          solution: "Developed an advanced scheduling system with overbooking capabilities, visual availability indicators, time-based service restrictions, and automated next-availability detection to optimize clinic operations."
        },
        {
          title: "WordPress Integration",
          solution: "Seamlessly integrated custom PHP functionality with WordPress hooks and filters, Elementor widgets for frontend customization, and maintained compatibility with existing WordPress plugins and themes."
        }
      ],
      screenshots: [
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/030f54f1-0ec2-4899-8b98-ceed4c1b2cc1.png",
          caption: "Kivicare Healthcare ERP dashboard"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/694b6bee-e7cf-4e7a-a54c-458a579acfdd.png",
          caption: "Advanced billing system with multiple bill types"
        }
      ]
    },
    {
      id: 6,
      title: "KiviCare - Masoomeh (Global clinic)",
      description: "Customization of KiviCare Healthcare ERP system with multi-timezone support, enhanced doctor and patient forms, advanced appointment filtering, and timezone-aware notifications for Masoomeh clinic.",
      fullDescription: `KiviCare Pro - Masoomeh is a comprehensive customization project for the KiviCare Healthcare ERP system, specifically tailored for Masoomeh clinic. The project focused on implementing multi-timezone support, enhancing user forms with dropdown selections, and creating advanced filtering capabilities for appointments.

The customization included adding timezone management throughout the system, allowing doctors to set their timezone and patients to view appointments in their local timezone. Enhanced filtering in both the appointment widget and dashboard appointment form enables users to filter by country, specialization, location, and doctor's language. The system also displays timezone-aware notifications showing times in both doctor's and patient's timezones.

Completed as a solo project, delivering all features within the deadline of October 3rd, 2024.`,
      category: "WordPress",
      technologies: ["WordPress", "PHP", "MySQL", "JavaScript", "jQuery", "REST API", "Timezone Management"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/ae15eeb4-5488-4653-b9b6-487357aaaa48.png",
      liveUrl: "https://global.clinic/",
      githubUrl: "",
      status: "completed",
      completedDate: "Oct 2024",
      duration: "1 month",
      teamSize: "1 developer",
      codeLines: "8,000+",
      performance: "99% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "Masoomeh clinic needed a customized KiviCare system that could handle multi-timezone operations, allowing doctors and patients from different countries to interact seamlessly. The system required enhanced filtering capabilities and timezone-aware notifications to improve user experience across different geographical locations.",
      solutionApproach: "Implemented comprehensive timezone management throughout the KiviCare system, enhanced doctor and patient forms with dropdown fields for country, timezone, and languages. Created advanced filtering system for appointments and ensured all notifications display times in both doctor's and patient's timezones for clarity.",
      keyFeatures: [
        "Multi-timezone support for doctors and patients",
        "Enhanced doctor form with country, timezone, and languages dropdowns",
        "Patient form with timezone dropdown selection",
        "Time slot management based on doctor's timezone",
        "Timezone-aware appointment display (doctor/patient/admin views)",
        "Advanced appointment widget filtering (Country → Specialization → Location → Language)",
        "Appointment form filtering modal in dashboard",
        "Dual timezone display in email notifications",
        "Doctor card enhancements with country flag and location display",
        "Seamless timezone conversion across all appointment views"
      ],
      challenges: [
        {
          title: "Multi-Timezone Management",
          solution: "Implemented timezone conversion logic throughout the system, storing doctor and patient timezones and converting appointment times dynamically based on user context. Ensured consistent time display across all views and notifications."
        },
        {
          title: "Advanced Filtering System",
          solution: "Created a cascading filter system in both the appointment widget and dashboard form, allowing users to filter by country, specialization, location, and doctor's language. Implemented efficient database queries to handle complex filtering scenarios."
        },
        {
          title: "Timezone-Aware Notifications",
          solution: "Modified email notification templates to display appointment times in both doctor's and patient's timezones, ensuring clarity for all parties regardless of their geographical location."
        },
        {
          title: "Form Enhancements",
          solution: "Replaced text input fields with dropdown menus for country, timezone, and languages, improving data consistency and user experience. Integrated timezone data and country flag display in doctor cards."
        }
      ],
      screenshots: [
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/1eae36d9-6800-4af7-b4d6-9481b5fa1b84.png",
          caption: "Enhanced doctor form with timezone and language dropdowns"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/e65a2ebd-9bea-4470-9e15-816f55a297b0.png",
          caption: "Appointment widget with advanced filtering options"
        }
      ]
    },
    {
      id: 7,
      title: "KiviCare - Dwayne (Antigua Med)",
      description: "Comprehensive customization of KiviCare Healthcare ERP plugin and mobile app with medical reports organization, patient forms system, medication tracking, secure messaging, and personalized notifications.",
      fullDescription: `KiviCare - Dwayne is an extensive customization project for the KiviCare Healthcare ERP system, focusing on enhancing patient management, medical records organization, and communication features. The project involved both web and API development to support a Flutter mobile application.

Key features include a sophisticated medical reports folder organization system allowing doctors to categorize and manage patient reports with clinic-specific structures and visibility controls. The system enables doctors to send forms to patients for completion, track medical conditions and medications, and facilitate secure messaging between doctors and patients.

Working alongside a Flutter developer, I handled all web-side and API-side development, ensuring seamless integration between the WordPress plugin backend and the mobile application. The project includes integration with third-party plugins for secure messaging and Twilio for personalized notifications via SMS and WhatsApp.`,
      category: "WordPress",
      technologies: ["WordPress", "PHP", "MySQL", "REST API", "JavaScript", "jQuery", "Twilio", "Flutter Integration"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/9167dc06-efbe-4992-8190-f53b921a3505.png",
      liveUrl: "https://antigua-med.com/",
      githubUrl: "",
      status: "completed",
      completedDate: "Dec 2024",
      duration: "2 months",
      teamSize: "2 developers",
      codeLines: "25,000+",
      performance: "99% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "The healthcare clinic needed enhanced patient management capabilities including organized medical reports, form submission workflows, medication tracking, secure patient-doctor communication, and personalized notification systems. The system required seamless integration between web and mobile platforms.",
      solutionApproach: "Developed comprehensive customization of the KiviCare plugin with folder-based medical reports organization, patient form submission system, medical conditions and medications tracking tabs, secure messaging integration, and Twilio-based personalized notifications. Created robust REST APIs to support Flutter mobile app integration.",
      keyFeatures: [
        "Medical Reports Folder Organization with clinic-specific structures",
        "Doctor-controlled folder visibility for patient access",
        "File upload functionality with folder categorization",
        "Patient form submission system with admin-created forms",
        "Medical Conditions tab for long-term diagnoses tracking",
        "My Medications tab with dosage, frequency, duration tracking",
        "Medication expiration tracking with automatic status updates",
        "Secure messaging system with third-party plugin integration",
        "Personalized notifications via SMS and WhatsApp using Twilio",
        "Clinic logo customization for dashboard and app",
        "REST API endpoints for Flutter mobile app integration",
        "Patient dashboard with category-wise report viewing"
      ],
      challenges: [
        {
          title: "Medical Reports Folder Organization",
          solution: "Implemented a hierarchical folder structure system allowing doctors to create and organize patient reports into categories. Added clinic-specific folder structures and visibility toggles to control patient access while maintaining organized medical records."
        },
        {
          title: "Form Submission Workflow",
          solution: "Created a dynamic form system where admins can create forms, doctors can send them to patients, and completed forms are automatically saved to patient records. Implemented notification system to alert patients when forms are assigned."
        },
        {
          title: "Medication Tracking System",
          solution: "Developed comprehensive medication management with fields for type, dosage, frequency, duration, and additional notes. Implemented automatic expiration tracking that moves completed medications to an expired section while maintaining historical records."
        },
        {
          title: "API Integration for Mobile App",
          solution: "Designed and developed RESTful APIs to support Flutter mobile app integration, ensuring seamless data synchronization between web and mobile platforms. Implemented secure authentication and data transfer protocols."
        },
        {
          title: "Multi-Channel Notifications",
          solution: "Integrated Twilio addon for SMS and WhatsApp notifications, allowing receptionists and doctors to send personalized notifications and reminders to patients through multiple channels."
        }
      ],
      screenshots: [
        {
          url: "https://ik.imagekit.io/nzc60yrceb/image%20(10).png?updatedAt=1763808180754",
          caption: "Medical reports folder organization interface"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/78267075-f3e9-447e-b8e3-ceaff871f19d.png",
          caption: "Medication tracking and medical conditions tabs"
        }
      ]
    },
    {
      id: 8,
      title: "KiviCare - Zander (Zapmed)",
      description: "Complete KiviCare Healthcare ERP customization with embedded doctor scheduling, automated patient onboarding, centralized patient database, custom prescription letterheads, and WordPress theme setup for Zapmed clinic.",
      fullDescription: `KiviCare - Zander (Zapmed) is a comprehensive customization and setup project for the KiviCare Healthcare ERP system. The project involved embedding doctor schedules directly on the website, implementing an automated patient onboarding system with Gravity Forms-like interface, creating a centralized patient database for multi-doctor access, and customizing prescription templates with doctor-specific letterheads.

The project included a complete WordPress theme and plugin setup, date format customization to DD/MM/YYYY throughout the system, and automated email notifications to system admin after appointment completion. The onboarding questionnaire automatically populates patient records, eliminating manual data entry and streamlining the patient registration process.

Completed as a solo project, delivering all features including the KiviCare theme bundle installation, demo content import, and full system configuration within the specified timeline.`,
      category: "WordPress",
      technologies: ["WordPress", "PHP", "MySQL", "Gravity Forms", "JavaScript", "jQuery", "REST API", "Email Integration", "Payment Gateway"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/30801fd8-1442-490a-a662-cbb700905c8a.png",
      liveUrl: "https://zapmed.co.za/",
      githubUrl: "",
      status: "completed",
      completedDate: "Jan 2025",
      duration: "1 week",
      teamSize: "1 developer",
      codeLines: "15,000+",
      performance: "99% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "Zapmed clinic needed a seamless patient onboarding and booking system with embedded doctor schedules on their website. The system required automated patient record creation from onboarding questionnaires, centralized patient database access for all doctors, custom prescription letterheads, and automated appointment completion notifications.",
      solutionApproach: "Developed a comprehensive solution embedding doctor schedules on the website with payment-integrated booking flow. Implemented automated patient onboarding system that creates patient profiles and populates records automatically. Created centralized patient database with multi-doctor access, custom prescription letterheads, and automated email notifications. Completed full WordPress theme and plugin setup with date format customization.",
      keyFeatures: [
        "Embedded doctor schedules and availability on website",
        "Automated patient onboarding with Gravity Forms-like interface",
        "Payment-required appointment booking system",
        "Auto-population of patient records from onboarding questionnaire",
        "Centralized patient database with multi-doctor access",
        "Custom prescription letterheads with doctor details",
        "Date format customization (DD/MM/YYYY) throughout system",
        "Automated email notifications to admin after appointment completion",
        "Patient profile auto-creation for future logins",
        "Skip onboarding for returning patients",
        "KiviCare WordPress theme and plugin bundle setup",
        "Demo content import and system configuration",
        "E-script email notifications with patient and doctor details"
      ],
      challenges: [
        {
          title: "Embedded Doctor Scheduling",
          solution: "Developed a seamless integration to display doctor schedules directly on the website, allowing patients to view availability and book appointments with payment confirmation. Implemented real-time availability updates and booking flow with payment gateway integration."
        },
        {
          title: "Automated Patient Onboarding",
          solution: "Created a professional onboarding questionnaire system similar to Gravity Forms that automatically creates patient profiles and populates medical records. Implemented logic to skip onboarding for returning patients while maintaining data integrity."
        },
        {
          title: "Centralized Patient Database",
          solution: "Implemented a shared patient database system allowing all doctors within the practice to access complete patient records, ensuring continuity of care when patients book with different doctors. Added secure access controls and patient report viewing capabilities."
        },
        {
          title: "Custom Prescription Letterheads",
          solution: "Developed a dynamic letterhead system that automatically pulls doctor details (name and practice number) from their profile and applies custom Zapmed letterhead templates to prescriptions in the encounter section."
        },
        {
          title: "System-Wide Date Format",
          solution: "Customized all date fields throughout the system to use DD/MM/YYYY format, including appointment bookings, prescription dates, and patient records, ensuring consistency across all modules."
        },
        {
          title: "Automated Email Notifications",
          solution: "Implemented automated email system that sends appointment completion notifications to system admin, including patient details, doctor details, and associated e-script information for record-keeping and administrative purposes."
        }
      ],
      screenshots: [
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/87f900da-8ba1-4576-bbd7-69b7ddc99d28.png",
          caption: "Embedded doctor schedules and booking interface"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/61cf8ed8-fb2a-41b0-b4e0-75554a54f933.png",
          caption: "Patient onboarding questionnaire and automated record creation"
        }
      ]
    },
    {
      id: 9,
      title: "KiviCare Pro - Stefano",
      description: "Customization of KiviCare Healthcare ERP system with flexible time slot management, clinic-wise date assignment, color-coded calendar views, enhanced appointment controls, and service description fields.",
      fullDescription: `KiviCare Pro - Stefano is a comprehensive customization project for the KiviCare Healthcare ERP system, focusing on advanced calendar and appointment management features. The project implemented flexible time slot management allowing Super Admins and Receptionists to assign custom time slots for specific dates from a calendar view.

The customization includes clinic-wise date assignment capabilities, enabling independent time slot management per clinic regardless of doctor availability. A color selection system was added to clinic forms, with colors reflected in the appointment calendar for easy visual identification. The calendar view was enhanced to display available/free slots for each clinic, with comprehensive filters for calendar and service navigation.

Completed as a solo project, delivering all features including admin appointment control for creating and modifying appointments directly from calendar slots, and service description fields visible during the booking phase.`,
      category: "WordPress",
      technologies: ["WordPress", "PHP", "MySQL", "JavaScript", "jQuery", "Calendar Integration", "REST API"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/f37d45b3-3fb4-4980-8569-b0de8ab2cd2d.png",
      liveUrl: "https://www.orthocarestp.it/",
      githubUrl: "",
      status: "completed",
      completedDate: "Jan 2025",
      duration: "2 weeks",
      teamSize: "1 developer",
      codeLines: "12,000+",
      performance: "99% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "The clinic needed advanced calendar management capabilities with flexible time slot assignment, clinic-specific scheduling independent of doctor availability, visual identification of clinics through color coding, and enhanced appointment control for administrators. The system required improved calendar views showing slot availability and comprehensive filtering options.",
      solutionApproach: "Developed a flexible time slot management system with calendar-based date selection, implemented clinic-wise date assignment with independent slot management, added color selection and visual representation in calendar views, enhanced calendar display with slot availability indicators, and created comprehensive filtering and admin appointment control features.",
      keyFeatures: [
        "Flexible time slot management with calendar date selection",
        "Super Admin and Receptionist access to time slot assignment",
        "Clinic-wise date assignment independent of doctor availability",
        "Color selection field in clinic form",
        "Color-coded appointment calendar for clinic identification",
        "Enhanced calendar view displaying available/free slots",
        "Calendar and service filters in left panel",
        "Comprehensive admin calendar view with appointments and free slots",
        "Admin appointment control - create/modify from calendar slots",
        "Service description field in SERVICE module",
        "Service descriptions visible during booking phase",
        "Monthly calendar view for time slot assignment"
      ],
      challenges: [
        {
          title: "Flexible Time Slot Management",
          solution: "Implemented a calendar-based interface allowing Super Admins and Receptionists to select specific dates from a monthly calendar view and assign custom time slots for each clinic. Created a user-friendly date picker with slot assignment functionality."
        },
        {
          title: "Clinic-Wise Date Assignment",
          solution: "Developed an independent scheduling system that allows assigning different available dates and time slots per clinic, completely separate from doctor availability. This enables clinics to have their own operational schedules."
        },
        {
          title: "Color-Coded Calendar System",
          solution: "Added color selection field to clinic forms and integrated color display throughout the appointment calendar. Implemented visual identification system making it easy to distinguish between different clinics at a glance."
        },
        {
          title: "Enhanced Calendar View with Slot Availability",
          solution: "Redesigned calendar view to clearly display available/free slots for each clinic. Implemented visual indicators and slot status tracking to help administrators and receptionists quickly identify booking opportunities."
        },
        {
          title: "Comprehensive Filtering System",
          solution: "Created calendar and service filters in the left panel, allowing users to quickly navigate and filter appointments by clinic, service type, and date range. Implemented efficient database queries for fast filtering performance."
        },
        {
          title: "Admin Appointment Control",
          solution: "Developed click-to-create functionality allowing admins to create or modify appointments directly by clicking on available/free slots in the calendar. Implemented modal interfaces for quick appointment creation and editing."
        },
        {
          title: "Service Description Integration",
          solution: "Added service description field to the SERVICE module and integrated it into the booking flow, ensuring patients can view service descriptions during the booking phase for better informed decision-making."
        }
      ],
      screenshots: [
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/e2324efc-7591-4d95-bb3a-2c933ff81adf.png",
          caption: "Flexible time slot management with calendar view"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/ee01a812-7c95-4a9a-99e7-7237ffce2cac.png",
          caption: "Color-coded calendar with slot availability and filters"
        }
      ]
    },
    {
      id: 10,
      title: "LandingTravels – Travel Agency Landing Page",
      description: "A modern, responsive landing page for a travel-agency / tour-operator concept built with static frontend technologies and deployed on Netlify.",
      fullDescription: "LandingTravels is a single-page, marketing-oriented website designed to showcase a travel/tourism business. The site features hero banners, featured tours or destinations, call-to-actions (e.g., “Book Now”), gallery or testimonial sections, and is optimized for conversion. It is likely built with a static site generator or modern frontend framework, uses modern CSS (possibly Tailwind or Bootstrap), and is deployed on a platform like Netlify for fast CDN delivery.\n\nBecause the content is focused on marketing rather than dynamic e-commerce or user-backend integration, the architecture is kept lightweight. The project emphasises good visuals, mobile responsiveness, performance, and ease of deployment.",
      category: "Frontend / Static Site",
      technologies: ["HTML5", "CSS3", "JavaScript", "Netlify", "Responsive Design", "Tailwind CSS"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/f0939cb1-48f0-468a-8fae-8b43fc713c4c.png",
      liveUrl: "https://landingtravels.netlify.app/",
      githubUrl: null,
      status: "completed",
      completedDate: "Nov 2025",
      duration: "2 weeks",
      teamSize: "1 developers",
      codeLines: "500 to 1,500",
      performance: "99% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "A travel-agency needed a clean, high-impact landing page that presents tours, builds trust and prompts bookings, without the complexity of a full web application.",
      solutionApproach: "Built a responsive static landing page using modern frontend stack and deployed on Netlify for fast global CDN, minimal backend overhead, and easy updates.",
      keyFeatures: [
        "Hero banner with strong call-to-action",
        "Featured tours/destinations section",
        "Gallery of traveller imagery",
        "Responsive layout across devices",
        "Fast static page load with CDN delivery",
        "Contact / enquiry section or form",
        "Smooth scroll navigation and anchor links",
        "Clear conversion-focused design"
      ],
      challenges: [
        {
          title: "High performance & mobile optimization",
          solution: "Minimized JavaScript, used responsive images, leveraged static hosting on CDN for fast page loads."
        },
        {
          title: "Visual impact & trust building",
          solution: "Included high quality imagery, testimonials or partner badges (if present), ensured clean layout and intuitive CTAs."
        }
      ],
      screenshots: [
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/00457f54-53ce-4e31-aaff-8fff0b4eeb96.png",
          caption: "LandingTravels website hero section"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/26c50688-aa46-4517-8fea-43215aeeba07.png",
          caption: "Featured tours/destinations section on LandingTravels"
        }
      ]
    },
    {
      id: 11,
      title: "InteriorLanding – Interior Design Landing Page",
      description: "A responsive, visually-driven landing page for an interior-design business, built with static frontend technologies and deployed on Netlify.",
      fullDescription: "InteriorLanding is a single-page marketing site aimed at interior design services. The site features large hero imagery, segmentation of service offerings (e.g., Modern, Contemporary, Minimalist, Industrial), pricing/product showcases, a testimonial section and a contact form. The architecture appears lightweight (static HTML/CSS/JS) and is optimized for hero visuals and conversion rather than complex interactivity or backend data flows.",
      category: "Frontend / Static Site",
      technologies: ["HTML5", "CSS3", "JavaScript", "Responsive Design", "Netlify Deployment"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/b9e07d33-6b79-4f26-a0df-eb644f884923.png",
      liveUrl: "https://interiorlanding.netlify.app/",
      githubUrl: null,
      status: "completed",
      completedDate: "Dec 2024",
      duration: "2 weeks",
      teamSize: "1 developer",
      codeLines: "500–1,000",
      performance: "High performance",
      stars: 0,
      forks: 0,
      problemStatement: "An interior-design firm needed a high-impact landing page to showcase services and convert visitors into leads without the overhead of a full-scale web application.",
      solutionApproach: "Developed a clean, single-page website focused on strong visual presentation, service categories, testimonial credibility and a contact/enquiry form. Deployed on Netlify for fast global delivery.",
      keyFeatures: [
        "Hero section with strong visual and headline",
        "Service categories: Modern, Contemporary, Minimalist, Industrial",
        "Featured product/service showcase with images and pricing",
        "Testimonials from clients",
        "Contact/enquiry form",
        "Responsive layout across device sizes",
        "Clear conversion-oriented call to action"
      ],
      challenges: [
        {
          title: "Balancing heavy imagery with performance",
          solution: "Used optimized image formats and limited JS to keep page load times low."
        },
        {
          title: "Creating trust & credibility",
          solution: "Added testimonials and service categories to show expertise and trusted work."
        }
      ],
      screenshots: [
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/92917a52-6065-4434-b093-3c6cd16ecb81.png",
          caption: "Hero section of InteriorLanding site"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/6925ecc6-7360-4eb8-8170-f6e1243e589a.png",
          caption: "Service categories section (Modern, Contemporary, etc.) on InteriorLanding"
        }
      ]
    },
    {
      id: 12,
      title: "ITCompanyLanding – IT Services / Tech Company Landing Page",
      description: "A clean, modern landing page designed for an IT services or tech-consulting company, built with static frontend technologies and deployed on Netlify.",
      fullDescription: "ITCompanyLanding is a single-page website aimed at showcasing a technology services business - likely including hero banner, service offerings (e.g., Web Development, App Development, UI/UX, SEO), partner/client logos, feature highlights, and a contact call-to-action. The architecture appears lightweight and marketing-oriented, with focus on visuals, responsiveness and simplicity rather than heavy backend interaction.",
      category: "Frontend / Static Site",
      technologies: ["HTML5", "CSS3", "JavaScript", "Responsive Design", "Netlify Deployment"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/4b7b4c63-64ef-4adb-acd3-fbb6e9357e54.png",
      liveUrl: "https://itcompanylanding.netlify.app/",
      githubUrl: null,
      status: "completed",
      completedDate: "Jan 2025",
      duration: "2 weeks",
      teamSize: "1 developer",
      codeLines: "500-1,000",
      performance: "99% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "A tech services firm needed a polished online presence to highlight its offerings, build credibility and convert website visitors to leads – without the complexity of a full web application.",
      solutionApproach: "Developed a sleek, single-page website with responsive layout, service highlight sections, client logos/testimonials (if present), and a clear contact or quotation form. Deployed via Netlify to leverage static delivery and minimal operational overhead.",
      keyFeatures: [
        "Hero section with strong headline & CTA",
        "Service categories (Web Development, App Development, UI/UX, SEO etc.)",
        "Client/Partner logos section",
        "Feature highlights or value propositions",
        "Responsive layout across devices",
        "Contact/enquiry section or form",
        "Smooth navigation/scrolling",
        "Conversion-oriented design"
      ],
      challenges: [
        {
          title: "Ensuring mobile-first responsiveness",
          solution: "Used flexible grid/stack layout and media queries to adapt across viewports while keeping visuals sharp."
        },
        {
          title: "Balancing strong visuals with load time",
          solution: "Optimised images, deferred non-critical JavaScript, used static hosting to reduce latency."
        }
      ],
      screenshots: [
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/e974a7e4-ab8a-43a6-b145-259cab7b79bb.png",
          caption: "Hero section of ITCompanyLanding website"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/d5892888-e401-4827-bfee-99e0fcb1abad.png",
          caption: "Service offering section on ITCompanyLanding"
        }
      ]
    },
    {
      id: 13,
      title: "HotelsLanding – Hotel & Resort Landing Page",
      description: "A sleek one-page landing website for a hotel/resort business, offering rooms, features, promotions, gallery and events sections, built with static frontend technologies and deployed on Netlify.",
      fullDescription: "HotelsLanding is a single-page marketing-oriented site designed to showcase a hotel or resort business. It features room categories (Standard, Family, Single, Deluxe, Luxury etc.), pricing per night, a hero section, features like swimming pool, minibar & airport car, a gallery, upcoming events section, testimonials, and a “Summer Promo” section with countdown. The architecture appears to be lightweight static HTML/CSS/JS, deployed via Netlify for fast global delivery.",
      category: "Frontend / Static Site",
      technologies: ["HTML5", "CSS3", "JavaScript", "Responsive Design", "Netlify Deployment"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/c31a1aba-39ce-480d-a350-1c1e6506e0fe.png",
      liveUrl: "https://hotelslanding.netlify.app/",
      githubUrl: null,
      status: "completed",
      completedDate: "Feb 2025",
      duration: "2 weeks",
      teamSize: "1 developer",
      codeLines: "500-1,500",
      performance: "99% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "A hotel/resort business needed a high-impact landing page to showcase room categories, features, special promotions, build credibility and convert visitors into bookings – without full application complexity.",
      solutionApproach: "Built a responsive static landing website with hero visuals, rooms/pricing section, hotel features, gallery, events & testimonials, with a strong call to action for booking or enquiry. Deployed via Netlify for minimal overhead and global CDN distribution.",
      keyFeatures: [
        "Hero section with strong headline & branding",
        "Rooms section with categories (Standard, Family, Single, Deluxe, Luxury) and price per night",
        "Hotel features section (Swimming Pool, Car Parking, Minibar, etc.)",
        "Gallery of images of the hotel/resort",
        "Events section showcasing upcoming events",
        "Testimonials/reviews section",
        "Promo section with countdown (e.g., Summer Promo 50% Off)",
        "Fully responsive & static – good for fast loading"
      ],
      challenges: [
        {
          title: "Presenting multiple room types with pricing clearly",
          solution: "Created distinct card-style room categories with pricing underneath, ensuring clarity and hierarchy."
        },
        {
          title: "Balancing large visuals (gallery, hero) with page load time",
          solution: "Optimised images, kept interactive scripts minimal, and leveraged static hosting."
        }
      ],
      screenshots: [
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/14ab419a-c36e-4e65-b578-e1129d730c0d.png",
          caption: "Rooms section listing different types and prices"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/0da2c668-5b74-4ccb-a3a6-f82f5e7d219b.png",
          caption: "Hotel features section with icons and descriptions"
        }
      ]
    },
    {
      id: 14,
      title: "BakerLanding – Corporate / Agency Landing Page",
      description: "A clean, multipurpose marketing-style landing page built with Bootstrap for a corporate or agency presentation, deployed via Netlify.",
      fullDescription: "BakerLanding is a static one-page website optimized for presenting services (e.g., web design, development, marketing, graphic design), team members, pricing plans, testimonials, contact form and a portfolio section. The layout uses a typical Bootstrap-based structure, includes multiple sections such as Hero, Services, Portfolio, Team, Pricing, Blog, Contact. The site appears to be a template or theme (“Designed by BootstrapMade”) and is likely meant for quick deployment for agencies or corporate businesses.",
      category: "Frontend / Static Site",
      technologies: ["HTML5", "CSS3", "Bootstrap", "JavaScript", "Responsive Design", "Netlify Deployment"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/175dfa5f-c372-4701-bcc4-ff52479a5d46.png",
      liveUrl: "https://bakerlanding.netlify.app/",
      githubUrl: null,
      status: "completed",
      completedDate: "July 2025",
      duration: "2 weeks",
      teamSize: "1 developer",
      codeLines: "500-1,500",
      performance: "99% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "An agency or corporate business needed a polished, multipurpose landing page to showcase services, team, pricing and contact options without building a full CMS or backend.",
      solutionApproach: "Developed a single‐page responsive website using Bootstrap, with sections for hero, services, clients/projects, team, pricing, blog highlights and contact. Deployed on Netlify for quick global hosting and minimal infrastructure overhead.",
      keyFeatures: [
        "Hero banner with headline & call-to-action",
        "Navigation with anchor links (Home, About, Service, Pricing, Team, Blog, Contact)",
        "Service offerings section (Web Design, Web Development, Product Management, Marketing, Graphic Design)",
        "Portfolio/gallery section filtering by category (All, App, Card, Web)",
        "Team section with photos and roles",
        "Pricing plans (Free, Business, Developer, Ultimate) with monthly pricing grid.",
        "Testimonials section with client quotes",
        "Contact section with address, phone, email and subscription form",
        "Responsive layout across devices and static hosting via Netlify"
      ],
      challenges: [
        {
          title: "Maintaining performance with many sections and images",
          solution: "Leverage Bootstrap’s grid and components, optimize images (though still a template), keep JavaScript minimal for anchor links and basic animations."
        },
        {
          title: "Providing clear conversion path",
          solution: "Placing 'Get Started' or contact CTA buttons at strategic sections (Hero, Services, Pricing) to prompt lead capture."
        }
      ],
      screenshots: [
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/8d4e27ba-4975-40e5-a3c7-1c36274239a2.png",
          caption: "Hero section of BakerLanding with headline and call-to-action"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/690e17d3-bbab-4393-8b8b-7fc528c7c918.png",
          caption: "Pricing plans section of BakerLanding"
        }
      ]
    },
    {
      id: 15,
      title: "LandingDemos – Hosting / Domain Service Landing Page",
      description: "A static marketing landing page tailored for a hosting/domains service provider, built with a straightforward frontend stack and deployed to Netlify.",
      fullDescription: "LandingDemos showcases a hosting and domain services business with features such as domain pricing, hosting plans, testimonials, service features and global support. The site appears to be a single‐page layout crafted for marketing conversion rather than extensive backend workflows.",
      category: "Frontend / Static Site",
      technologies: ["HTML5", "CSS3", "JavaScript", "Responsive Design", "Netlify Deployment"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/ac495d1d-f345-4948-af6e-1c8fc657aeeb.png",
      liveUrl: "https://landingdemos.netlify.app/",
      githubUrl: null,
      status: "completed",
      completedDate: "Sep 2025",
      duration: "2 weeks",
      teamSize: "1 developer",
      codeLines: "500-1000",
      performance: "upto 95%",
      stars: 0,
      forks: 0,
      problemStatement: "A hosting/domains provider needed a convincing online presence to display services, pricing and testimonials and turn visitors into leads-without full application complexity.",
      solutionApproach: "Developed a lean, responsive landing page featuring hero banners, service plans (domain + hosting), feature overview, testimonials, and global contact info. Deployed on Netlify for fast global content delivery with minimal maintenance.",
      keyFeatures: [
        "Hero section with domain & hosting banner and pricing teaser",
        "Plan section showing Shared, Dedicated, Premium, Cloud Hosting options (with starting prices) as seen on site.",
        "Why-Choose-Us section with service features (High Quality Hardware, Top Data Centers, Outstanding Speed, Amazing Uptime, Best Support)",
        "Testimonials section with client quotes and photos.",
        "Global contact/address section showing Europe & USA offices.",
        "Simple navigation with links: Home, Domain, Hosting, Blog, Contact."
      ],
      challenges: [
        {
          title: "Balancing heavy hero imagery and multiple sections with load performance",
          solution: "Used static hosting and likely optimised assets to keep page load times low."
        },
        {
          title: "Conveying credibility for hosting services with minimal backend",
          solution: "Added testimonials, review scores (“We are rated 4.99 / 5.00 (based on 4443 Reviews)”) and included global office contact details."
        }
      ],
      screenshots: [
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/52d24031-c6d4-4fc5-838f-6d097c93cf76.png",
          caption: "Hero banner section of LandingDemos"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/2393f479-73de-46f2-ada9-93057c94a5c9.png",
          caption: "Why Choose Us / Features section of LandingDemos"
        }
      ]
    },
    {
      id: 16,
      title: "IceCuubes – Professional Website for IT Services",
      description: "A visually-oriented landing page built and deployed on Netlify, focused on a clean design with strong hero visuals and conversion-focused content.",
      fullDescription: "IceCuubes is a single-page marketing-style website deployed via Netlify. It features a large hero section, branding/messaging around “ice” or crisp imagery, likely built to showcase a product or service in a sleek way. The architecture appears to be a static frontend site, optimized for quick deployment and performance.",
      category: "Frontend",
      technologies: ["HTML5", "CSS3", "JavaScript", "Responsive Design", "Netlify Deployment"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/c0234bc4-1a28-45f6-bbd7-3da5d1e998e8.png",    /* (assumed path) */
      liveUrl: "https://icecuubes.netlify.app/",
      githubUrl: null,
      status: "completed",
      completedDate: "Oct 2025",
      duration: "2 weeks",
      teamSize: "1 developer",
      codeLines: "500-1,000",
      performance: "90% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "A modern business/product required a high-impact landing page to present branding, features and prompt visitor conversion - without building a full backend application.",
      solutionApproach: "Developed a static single-page site with strong visual hero, key features/benefits section, call to action, responsive layout across devices, and deployed via Netlify for global fast delivery.",
      keyFeatures: [
        "Hero section with prominent imagery and brand headline",
        "Features/benefits section highlighting what’s offered",
        "Call-to-action button encouraging user engagement",
        "Responsive layout for mobile and desktop",
        "Optimized static hosting (no heavy backend) for speed and simplicity"
      ],
      challenges: [
        {
          title: "Ensuring fast page load with heavy visual content",
          solution: "Optimised images, limited heavy JS, used static hosting via Netlify CDN."
        },
        {
          title: "Conveying product/service value on a single page",
          solution: "Structured content into clear headings, benefit statements, and a strong CTA to drive visitor action."
        }
      ],
      screenshots: [
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/2e10a4b1-3e67-4801-a600-58da9eedfd3b.png",
          caption: "Hero section of IceCuubes Website page"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/0d692a93-d5a5-40c8-b593-e0998b78005e.png",
          caption: "Features/benefits section on IceCuubes site"
        }
      ]
    },
    {
      id: 17,
      title: "BizzPages – Business/Corporate Landing Page",
      description: "A sleek landing page template for a business or corporate service offering, built with static frontend technologies and deployed on Netlify.",
      fullDescription: "BizzPages is a single-page marketing website designed for a business/agency context. It features large hero visuals, service highlight sections, client testimonials or logos, and a contact/lead-capture section. The architecture appears to be lightweight and focuses on marketing conversion rather than deep application logic.",
      category: "Frontend / Static Site",
      technologies: ["HTML5", "CSS3", "JavaScript", "Responsive Design", "Static Site (Netlify)"],
      image: "https://res.cloudinary.com/dnv6oqvjp/image/upload/20bfc0ef-7c3f-49a6-9aec-710c993958a1.png",  // (assumed path, verify actual)
      liveUrl: "https://bizzpages.netlify.app/",
      githubUrl: null,
      status: "completed",
      completedDate: "Nov 2025",
      duration: "2 weeks",
      teamSize: "1 developer",
      codeLines: "500-1,000",
      performance: "97% uptime",
      stars: 0,
      forks: 0,
      problemStatement: "A business service or agency needed a professional online presence to showcase services, build trust, and convert visitors into leads without building a full-scale web application.",
      solutionApproach: "Created a responsive static landing page using modern HTML/CSS/JavaScript, with hero section, service offerings, testimonial/logos section, contact form or call-to-action, and deployed via Netlify for global distribution and minimal maintenance.",
      keyFeatures: [
        "Hero banner with headline and call-to-action",
        "Services section (multiple offerings) with icons or images",
        "Client/logo/testimonial section to build credibility",
        "About/why-choose-us section",
        "Contact/enquiry form or CTA",
        "Responsive layout across devices",
        "Static deployment (fast load, low overhead)"
      ],
      challenges: [
        {
          title: "Delivering a strong visual first-impression for business clients",
          solution: "Used high-impact hero imagery and clean professional typography/color palette to align with corporate branding."
        },
        {
          title: "Ensuring quick load times despite rich visuals",
          solution: "Leveraged static hosting with CDN (Netlify) and optimised assets (images, minimal JS) to keep performance high."
        }
      ],
      screenshots: [
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/7aa59e88-ba16-4216-aa79-d626c1e65100.png",
          caption: "Hero section of BizzPages landing page"
        },
        {
          url: "https://res.cloudinary.com/dnv6oqvjp/image/upload/1e924692-e5b3-49c6-af9c-4f13b300beca.png",
          caption: "Services section on BizzPages showing offerings"
        }
      ]
    },
  ];

  // Filter options
  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'Full Stack', label: 'Full Stack' },
    { value: 'Frontend', label: 'Frontend' },
    { value: 'Mobile', label: 'Mobile' },
    { value: 'Backend Development', label: 'Backend Development' },
    { value: 'WordPress', label: 'WordPress' },
    { value: 'Frontend / Static Site', label: 'Landing Page' },
  ];

  const technologies = [
    { value: 'all', label: 'All Technologies' },
    ...Array.from(new Set(projects.flatMap(p => p.technologies)))
      .sort()
      .map(tech => ({ value: tech, label: tech }))
  ];

  const statuses = [
    { value: 'all', label: 'All Statuses' },
    { value: 'completed', label: 'Completed' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'planned', label: 'Planned' }
  ];

  // Filter projects
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.technologies.some(tech => tech.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = selectedCategory === 'all' || project.category === selectedCategory;
    const matchesTechnology = selectedTechnology === 'all' || project.technologies.includes(selectedTechnology);
    const matchesStatus = selectedStatus === 'all' || project.status === selectedStatus;

    return matchesSearch && matchesCategory && matchesTechnology && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);
  const startIndex = (currentPage - 1) * projectsPerPage;
  const paginatedProjects = filteredProjects.slice(startIndex, startIndex + projectsPerPage);

  // Handlers
  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedTechnology('all');
    setSelectedStatus('all');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Loading simulation
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setIsLoading(false);
  //   }, 1000);

  //   return () => clearTimeout(timer);
  // }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedTechnology, selectedStatus]);


  return (
    <>
      <Helmet>
        <title>Project Portfolio - Dishant Madariya | Full Stack Developer</title>
        <meta
          name="description"
          content="Explore the project portfolio of Full Stack Developer Dishant Madariya, showcasing MERN stack, WordPress, SaaS, and high-performance landing page projects."
        />
        <meta
          name="keywords"
          content="Dishant Madariya projects, portfolio, full stack projects, React, Node.js, WordPress, SaaS development, web development"
        />
        <link rel="canonical" href="https://port-folio-three-teal.vercel.app/projects" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <SectionScrollIndicator />

        <main className="pt-16">
          {/* Hero Section */}
          <section className="py-16 px-6 lg:px-8 bg-gradient-to-br from-primary/5 to-accent/5">
            <div className="max-w-7xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-4xl lg:text-6xl font-bold text-text-primary mb-6">
                  Project Portfolio
                </h1>
                <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">
                  Explore my comprehensive collection of projects showcasing full-stack development skills,
                  innovative solutions, and technical expertise across various domains and technologies.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    variant="default"
                    size="lg"
                    onClick={() => document.getElementById('projects-section').scrollIntoView({ behavior: 'smooth' })}
                    iconName="ArrowDown"
                    iconPosition="right"
                  >
                    View Projects
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => window.open('https://github.com/DishantMadariya', '_blank')}
                    iconName="Github"
                    iconPosition="left"
                  >
                    GitHub Profile
                  </Button>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Projects Section */}
          <section id="projects-section" className="py-16 px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              {/* Project Stats */}
              <ProjectStats projects={projects} />

              {/* Project Filter */}
              <ProjectFilter
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                selectedTechnology={selectedTechnology}
                onTechnologyChange={setSelectedTechnology}
                selectedStatus={selectedStatus}
                onStatusChange={setSelectedStatus}
                onClearFilters={handleClearFilters}
                categories={categories}
                technologies={technologies}
                statuses={statuses}
                totalProjects={projects.length}
                filteredCount={filteredProjects.length}
              />

              {/* Projects Grid */}
              {filteredProjects.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                    {paginatedProjects.map((project, index) => (
                      <motion.div
                        key={project.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <ProjectCard
                          project={project}
                          onViewDetails={handleViewDetails}
                        />
                      </motion.div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        iconName="ChevronLeft"
                        iconPosition="left"
                      >
                        Previous
                      </Button>

                      <div className="flex space-x-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            className="w-10"
                          >
                            {page}
                          </Button>
                        ))}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        iconName="ChevronRight"
                        iconPosition="right"
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-16"
                >
                  <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-text-primary mb-2">No projects found</h3>
                  <p className="text-text-secondary mb-6">
                    Try adjusting your search criteria or clearing the filters.
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleClearFilters}
                    iconName="RotateCcw"
                    iconPosition="left"
                  >
                    Clear Filters
                  </Button>
                </motion.div>
              )}
            </div>
          </section>

          {/* Call to Action */}
          <section className="py-16 px-6 lg:px-8 bg-gradient-to-r from-primary/10 to-accent/10">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl font-bold text-text-primary mb-6">
                  Ready to Start Your Next Project?
                </h2>
                <p className="text-lg text-text-secondary mb-8">
                  Let's collaborate and bring your ideas to life with cutting-edge technology and innovative solutions.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    variant="default"
                    size="lg"
                    onClick={() => navigate('/contact')}
                    iconName="Mail"
                    iconPosition="left"
                  >
                    Get In Touch
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => window.open('https://github.com/DishantMadariya', '_blank')}
                    iconName="Github"
                    iconPosition="left"
                  >
                    View All Code
                  </Button>
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        {/* Project Detail Modal */}
        <ProjectDetailModal
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      </div>
    </>
  );
};

export default ProjectPortfolio;